/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import useStoreFormDraft from "../../../hooks/useStoreFormDraft";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { applyStoreDraft } from "../../../redux/store/store";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import { doc } from "firebase/firestore";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import {
  Check,
  RefreshCw,
  Info,
  X,
  AlertCircle,
  Facebook,
  Activity,
  Key,
  Code,
} from "lucide-react";
import apicache from "../../../components/clearCach/clear";
import { useTranslation } from "react-i18next";

export default function ActivationFacebookApi({ close }) {
  const store = useSelector((state) => state.StoreReducer);
  const dispatch = useDispatch();
  const products = useSelector((state) => state.ProductsReducer);
  const account = useSelector((state) => state.AccountReducer);
  const MySwal = withReactContent(Swal);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useStoreFormDraft(store);
  const [isApiActive, setIsApiActive] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {

    // Check if the API is active by checking if both required fields have values
    setIsApiActive(Boolean(store?.Facebookapi));
  }, [store]);

  // Add SweetAlert styling for z-index
  useEffect(() => {
    // Add CSS to make sure SweetAlert appears on top of other modals
    const style = document.createElement("style");
    style.innerHTML = `
      .swal-container-on-top {
        z-index: 9999999999 !important;
      }
      .swal-backdrop-on-top {
        background: rgba(0,0,0,0.7) !important;
      }
      .swal-popup-on-top {
        position: relative;
        box-shadow: 0 0 20px rgba(0,0,0,0.2);
      }
      body.swal2-shown {
        overflow: hidden;
      }
      body.swal2-shown > [aria-hidden="true"] {
        filter: blur(3px);
      }
    `;
    document.head.appendChild(style);

    // Add a click handler to ensure modal shows on top
    const handleDocumentClick = () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "9999999999";
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.head.removeChild(style);
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: t(
        "servicePageActivation.servicePageFacebookPixel.alerts.confirm"
      ),
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `
        rgba(0,0,0,0.7)
      `,
      position: "center",
      customClass: {
        popup: "swal-popup-on-top",
        backdrop: "swal-backdrop-on-top",
        container: "swal-container-on-top",
      },
      willOpen: () => {
        // Ensure this alert appears on top of everything
        document.querySelector(".swal2-container").style.zIndex = "9999999999";
      },
    });
  };

  // const clearPixelId = () => {
  //   setData({ ...data, facebookPixelapi: "" });
  // };

  const clearAccountToken = () => {
    setData({ ...data, Facebookapi: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if document ID exists
    if (!data.id) {
      await showAlert(
        "warning",
        t(
          "servicePageActivation.facebookApiConversion.alerts.warningMissingIdTitle"
        ),
        t(
          "servicePageActivation.facebookApiConversion.alerts.warningMissingIdText"
        )
      );
      return;
    }

    // Check if we're activating or deactivating
    const isDeactivating = !data.Facebookapi;

    // If activating, validate both fields
    if (!isDeactivating) {
      // Form validation
      if (!data.Facebookapi) {
        await showAlert(
          "warning",
          t(
            "servicePageActivation.facebookApiConversion.alerts.warningMissingPixelTitle"
          ),
          t(
            "servicePageActivation.facebookApiConversion.alerts.warningMissingPixelText"
          )
        );
        return;
      }
    } else {
      // If deactivating, confirm with user
      const result = await showAlert(
        "warning",
        t(
          "servicePageActivation.facebookApiConversion.alerts.confirmDeactivationTitle"
        ),
        t(
          "servicePageActivation.facebookApiConversion.alerts.confirmDeactivationText"
        )
      );

      // If user cancels the confirmation, stop the process
      if (!result.isConfirmed) {
        return;
      }

    }

    setLoading(true);

    try {
      const payload = isDeactivating ? { ...data, Facebookapi: "" } : data;
      await saveStoreDraft(doc(db, "store", data.id), store, payload);
      dispatch(applyStoreDraft(store, payload));
      setData(payload);
      setLoading(false);
      setIsApiActive(!isDeactivating);

      if (isDeactivating) {
        await showAlert(
          "success",
          t(
            "servicePageActivation.facebookApiConversion.alerts.successDeactivationTitle"
          ),
          ""
        );
      } else {
        await showAlert(
          "success",
          t(
            "servicePageActivation.facebookApiConversion.alerts.successActivationTitle"
          ),
          ""
        );
      }
      close();
      await apicache(account, products);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setLoading(false);

      if (isDeactivating) {
        await showAlert(
          "error",
          t(
            "servicePageActivation.facebookApiConversion.alerts.errorDeactivationTitle"
          ),
          t(
            "servicePageActivation.facebookApiConversion.alerts.errorGenericText"
          )
        );
      } else {
        await showAlert(
          "error",
          t(
            "servicePageActivation.facebookApiConversion.alerts.errorActivationTitle"
          ),
          t(
            "servicePageActivation.facebookApiConversion.alerts.errorGenericText"
          )
        );
      }
    }
  };

  return (
    <div className="min-h-[400px] h-fit p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#9e68ba] mb-2 flex items-center">
          <Facebook className="ml-2" size={20} />
          {t("servicePageActivation.facebookApiConversion.title")}
        </h1>
        <p className="text-gray-600 dark:!text-gray-400">
          {t("servicePageActivation.facebookApiConversion.description")}
        </p>
      </div>

      {/* Service Status Indicator */}
      <div
        className={`mb-4 p-3 rounded-lg ${
          isApiActive
            ? "bg-green-50 border border-green-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
            : "bg-gray-50 border border-gray-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
        }`}
      >
        <div className="flex items-center gap-x-2">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${
              isApiActive ? "bg-green-500" : "bg-gray-400 dark:!bg-gray-500"
            }`}
          ></div>
          <span
            className={`text-sm font-medium ${
              isApiActive
                ? "text-green-700"
                : "text-gray-700 dark:!text-gray-300"
            }`}
          >
            {isApiActive
              ? t("servicePageActivation.facebookApiConversion.status.active")
              : t(
                  "servicePageActivation.facebookApiConversion.status.inactive"
                )}
          </span>
        </div>
      </div>

      {/* Activation Form */}
      <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#9e68ba] mb-4 flex items-center">
              <Activity className="ml-2" size={18} />
              {t(
                "servicePageActivation.facebookApiConversion.form.sectionTitle"
              )}
            </h3>

            {/* Pixel ID Input */}
            {/* <div className="mb-5">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:!text-gray-300">
                  {t(
                    "servicePageActivation.facebookApiConversion.form.pixelIdLabel"
                  )}
                  <span className="text-red-500">*</span>
                </label>
                <div
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    data.facebookPixelapi
                      ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                      : "bg-gray-100 text-gray-800 dark:!bg-gray-700 dark:!text-gray-300"
                  }`}
                >
                  {data.facebookPixelapi
                    ? t(
                        "servicePageActivation.facebookApiConversion.form.pixelIdEntered"
                      )
                    : t(
                        "servicePageActivation.facebookApiConversion.form.pixelIdEntered"
                      )}
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t(
                    "servicePageActivation.facebookApiConversion.form.pixelIdPlaceholder"
                  )}
                  value={data.facebookPixelapi || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setData({ ...data, facebookPixelapi: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 dark:!bg-[#141414] dark:!text-gray-200 pr-10"
                />
                {data.facebookPixelapi && (
                  <button
                    type="button"
                    onClick={clearPixelId}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-gray-600"
                    title={t("servicePageActivation.tiktokPixel.clearTitle")}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div> */}

            {/* Account Token Input */}
            <div className="mb-5">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:!text-gray-300">
                  {t(
                    "servicePageActivation.facebookApiConversion.form.accountTokenLabel"
                  )}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    data.Facebookapi
                      ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                      : "bg-gray-100 text-gray-800 dark:!bg-gray-700 dark:!text-gray-300"
                  }`}
                >
                  {data.Facebookapi
                    ? t(
                        "servicePageActivation.facebookApiConversion.form.accountTokenEntered"
                      )
                    : t(
                        "servicePageActivation.facebookApiConversion.form.accountTokenMissing"
                      )}
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t(
                    "servicePageActivation.facebookApiConversion.form.accountTokenPlaceholder"
                  )}
                  value={data.Facebookapi || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setData({ ...data, Facebookapi: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 dark:!bg-[#141414] dark:!text-gray-200 pr-10"
                />
                {data.Facebookapi && (
                  <button
                    type="button"
                    onClick={clearAccountToken}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-gray-600"
                    title={t("servicePageActivation.tiktokPixel.clearTitle")}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {!data.Facebookapi && (
                <span className="text-amber-600 text-sm mt-1 block">
                  {t(
                    "servicePageActivation.facebookApiConversion.form.missingAccountTokenWarning"
                  )}
                </span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 ${
                data.Facebookapi
                  ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                  : isApiActive
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-gray-400"
              } text-white font-medium rounded-lg transition-colors flex items-center ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <RefreshCw className="animate-spin ml-2" size={18} />
                  {data.Facebookapi
                    ? t(
                        "servicePageActivation.facebookApiConversion.form.submit.activating"
                      )
                    : t(
                        "servicePageActivation.facebookApiConversion.form.submit.deactivating"
                      )}
                </>
              ) : (
                <>
                  {data.Facebookapi ? (
                    <>
                      <Check className="ml-2" size={18} />
                      {t(
                        "servicePageActivation.facebookApiConversion.form.submit.activate"
                      )}
                    </>
                  ) : isApiActive ? (
                    <>
                      <X className="ml-2" size={18} />
                      {t(
                        "servicePageActivation.facebookApiConversion.form.submit.deactivate"
                      )}
                    </>
                  ) : (
                    <>
                      <AlertCircle className="ml-2" size={18} />
                      {t(
                        "servicePageActivation.facebookApiConversion.form.submit.enterIds"
                      )}
                    </>
                  )}
                </>
              )}
            </button>

            <div className="mt-3 text-sm text-gray-600 dark:!text-gray-400">
              <p>
                {t(
                  "servicePageActivation.facebookApiConversion.form.instructions.0"
                )}
              </p>
              <p>
                {t(
                  "servicePageActivation.facebookApiConversion.form.instructions.1"
                )}
              </p>
            </div>
          </div>
        </form>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 dark:!bg-[#181919] rounded-xl p-5 border border-blue-100 dark:!border-[#3c3a3d] mb-6">
        <h3 className="text-lg font-semibold text-blue-800 dark:!text-gray-200 mb-3 flex items-center">
          <Info className="ml-2" size={18} />
          {t("servicePageActivation.facebookApiConversion.infoBox.title")}
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-700 dark:!text-gray-400 mr-2">
          <li className="flex items-start">
            <span className="ml-2 inline-block">
              <Facebook
                size={16}
                className="text-blue-600 mt-1 dark:!text-gray-300"
              />
            </span>
            <span>
              {t("servicePageActivation.facebookApiConversion.infoBox.steps.0")}
            </span>
          </li>
          <li className="flex items-start">
            <span className="ml-2 inline-block">
              <Activity
                size={16}
                className="text-blue-600 mt-1 dark:!text-gray-300"
              />
            </span>
            <span>
              {t("servicePageActivation.facebookApiConversion.infoBox.steps.1")}
            </span>
          </li>
          <li className="flex items-start">
            <span className="ml-2 inline-block">
              <Code
                size={16}
                className="text-blue-600 mt-1 dark:!text-gray-300"
              />
            </span>
            <span>
              {t("servicePageActivation.facebookApiConversion.infoBox.steps.2")}
            </span>
          </li>
          <li className="flex items-start">
            <span className="ml-2 inline-block">
              <Check
                size={16}
                className="text-blue-600 mt-1 dark:!text-gray-300"
              />
            </span>
            <span>
              {t("servicePageActivation.facebookApiConversion.infoBox.steps.3")}
            </span>
          </li>
          <li className="flex items-start">
            <span className="ml-2 inline-block">
              <Key
                size={16}
                className="text-blue-600 mt-1 dark:!text-gray-300"
              />
            </span>
            <span>
              {t("servicePageActivation.facebookApiConversion.infoBox.steps.4")}
            </span>
          </li>
          <li className="flex items-start">
            <span className="ml-2 inline-block">
              <Check
                size={16}
                className="text-blue-600 mt-1 dark:!text-gray-300"
              />
            </span>
            <span>
              {t("servicePageActivation.facebookApiConversion.infoBox.steps.5")}
            </span>
          </li>
        </ol>
      </div>

      {/* Benefits Box */}
      <div className="bg-green-50 dark:!bg-[#181919] rounded-xl p-5 border border-green-100 dark:!border-[#3c3a3d]">
        <h3 className="text-lg font-semibold text-green-800 dark:!text-gray-200 mb-3 flex items-center">
          <Check className="ml-2" size={18} />
          {t("servicePageActivation.facebookApiConversion.benefitsBox.title")}
        </h3>
        <ul className="list-disc list-inside space-y-2 text-green-700 dark:!text-gray-400 mr-2">
          <li>
            {t(
              "servicePageActivation.facebookApiConversion.benefitsBox.points.0"
            )}
          </li>
          <li>
            {t(
              "servicePageActivation.facebookApiConversion.benefitsBox.points.1"
            )}
          </li>
          <li>
            {t(
              "servicePageActivation.facebookApiConversion.benefitsBox.points.2"
            )}
          </li>
          <li>
            {t(
              "servicePageActivation.facebookApiConversion.benefitsBox.points.3"
            )}
          </li>
          <li>
            {t(
              "servicePageActivation.facebookApiConversion.benefitsBox.points.4"
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}
